<script>
import { describeQuotedMessage } from 'dashboard/helper/quotedMessagePreview';
import QuotedMediaIcon from 'dashboard/components-next/message/QuotedMediaIcon.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { MAX_PINNED_MESSAGES } from 'dashboard/helper/pinnedMessages';

// Aceleres 2026-09-01: no banner da mensagem fixada, midia nao mostra preview
// de texto — mostra o icone do tipo e a palavra ao lado do alfinete, como o
// WhatsApp faz na barra fixa. Os rotulos sao os pedidos ("Imagem", e nao o
// "Foto" que o card de citacao usa, vindo de CHAT_LIST.ATTACHMENTS.image).
const MEDIA_LABELS = {
  image: 'Imagem',
  video: 'Vídeo',
  gif: 'GIF',
};

export default {
  name: 'ConversationPinnedMessage',
  components: { QuotedMediaIcon, DropdownMenu },
  props: {
    // Ate MAX_PINNED_MESSAGES mensagens fixadas, na ordem em que foram
    // fixadas. Cada item e o instantaneo guardado na conversa ja reconciliado
    // com a mensagem viva quando ela esta carregada (ver MessagesView).
    pinnedMessages: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['unpin', 'scroll-to'],
  data() {
    return {
      // Indice do item que o banner mostra. Comeca na primeira fixada e cada
      // clique anda um passo, em ciclo, como no WhatsApp.
      activeIndex: 0,
      // O menu da faixa (Desafixar / Ir para a mensagem) esta aberto?
      isMenuOpen: false,
    };
  },
  computed: {
    items() {
      return this.pinnedMessages.slice(0, MAX_PINNED_MESSAGES);
    },
    itemIds() {
      return this.items.map(item => item.id).join(',');
    },
    // O indice fica sempre dentro da lista: desafixar o ultimo item nao pode
    // deixar o banner apontando para um lugar que ja nao existe.
    safeIndex() {
      if (!this.items.length) return 0;
      return Math.min(Math.max(this.activeIndex, 0), this.items.length - 1);
    },
    activeMessage() {
      return this.items[this.safeIndex] || null;
    },
    // Reaproveita o descritor da mensagem citada em vez de reinventar a
    // deteccao de tipo: e ele que distingue GIF de video e sabe desenhar o
    // icone certo (QuotedMediaIcon desenha foto e GIF a mao, porque o
    // conjunto lucide nao tem esses dois tracos).
    described() {
      if (!this.activeMessage) return null;
      return describeQuotedMessage(this.activeMessage, this.$t.bind(this));
    },
    mediaKind() {
      const kind = this.described?.kind;
      return kind && MEDIA_LABELS[kind] ? kind : '';
    },
    label() {
      if (this.mediaKind) return MEDIA_LABELS[this.mediaKind];
      // Midia sem rotulo proprio (audio, documento, figurinha...) usa o rotulo
      // que o descritor ja monta — nome do arquivo, duracao do audio.
      if (this.described?.kind)
        return this.described.label || 'Mensagem fixada';

      const content = this.activeMessage?.content || '';
      if (!content) return 'Mensagem fixada';
      // Uma linha so: quem corta e o `truncate` do CSS, que sabe a largura
      // real. Aqui so tiramos as quebras que virariam espaco em branco morto.
      return content.replace(/\s+/g, ' ').trim();
    },
    // As duas acoes da faixa, no formato que o DropdownMenu espera. Desafixar
    // vem primeiro, como no WhatsApp: e a razao pela qual se abre este menu.
    menuItems() {
      return [
        {
          action: 'unpin',
          value: 'unpin',
          label: 'Desafixar',
          icon: 'i-lucide-pin-off',
        },
        {
          action: 'scroll-to',
          value: 'scroll-to',
          label: 'Ir para a mensagem',
          icon: 'i-lucide-arrow-right',
        },
      ];
    },
  },
  watch: {
    // Desafixar encolhe a lista: se o ponteiro ficou fora dela, volta ao
    // primeiro item. A troca de conversa nao passa por aqui — o componente e
    // recriado por `:key` no MessagesView, entao o indice ja nasce zerado.
    itemIds() {
      if (this.activeIndex > this.items.length - 1) {
        this.activeIndex = 0;
      }
    },
  },
  methods: {
    // Um clique = ir ate a fixada exibida e, ja que estamos la, adiantar o
    // ponteiro para a proxima. E o comportamento do WhatsApp: clicando em
    // sequencia o agente percorre as fixadas sem procurar na conversa.
    handleClick() {
      const message = this.activeMessage;
      if (!message) return;

      this.$emit('scroll-to', message.id);
      if (this.items.length > 1) {
        this.activeIndex = (this.safeIndex + 1) % this.items.length;
      }
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    // Botao direito em qualquer ponto da faixa abre o mesmo menu. O `prevent`
    // tira o menu do navegador da frente; sem o `stop`, o clique continuaria
    // ate `handleClick` e a faixa saltava para a mensagem por baixo do menu
    // que acabou de abrir.
    openMenu() {
      this.isMenuOpen = true;
    },
    closeMenu() {
      this.isMenuOpen = false;
    },
    // O menu fecha sempre: as duas acoes tiram da tela o que ele descrevia --
    // desafixar apaga o item, e ir ate a mensagem leva o agente para longe da
    // faixa.
    //
    // "Ir para a mensagem" reaproveita `handleClick`, o mesmo caminho do clique
    // no corpo da faixa: alem do salto, ele adianta o ponteiro para a proxima
    // fixada. Dois caminhos para o mesmo destino acabariam por divergir.
    handleMenuAction(item) {
      this.closeMenu();
      if (!this.activeMessage) return;

      if (item.action === 'unpin') {
        this.$emit('unpin', this.activeMessage.id);
        return;
      }
      this.handleClick();
    },
  },
};
</script>

<template>
  <div
    v-if="activeMessage"
    class="aceleres-pinned-banner flex items-center gap-2 px-3 py-2 border-b cursor-pointer transition-colors"
    role="button"
    :title="label"
    @click.stop="handleClick"
    @contextmenu.prevent.stop="openMenu"
  >
    <span
      class="acl-pinned-banner__marks flex flex-col justify-center flex-shrink-0"
      :aria-label="`${items.length} fixada(s), a ${safeIndex + 1} em exibição`"
    >
      <i
        v-for="(item, index) in items"
        :key="item.id"
        class="acl-pinned-banner__mark"
        :class="{ 'acl-pinned-banner__mark--active': index === safeIndex }"
      />
    </span>
    <span class="i-lucide-pin acl-pinned-banner__pin flex-shrink-0" />
    <p
      class="acl-pinned-banner__label flex items-center gap-1.5 min-w-0 text-xs mb-0"
    >
      <QuotedMediaIcon
        v-if="described && described.icon"
        :kind="described.kind"
        :icon="described.icon"
      />
      <span class="truncate min-w-0">{{ label }}</span>
    </p>
    <!--
      Pedido 2026-09-14 (facundo): o gatilho do menu volta, agora como botao
      REDONDO com a seta para baixo — o quadrado de 20px de antes nao era lido
      como botao no telemovel. O fundo cinza redondo aparece ao passar o rato
      (`hover:bg-n-alpha-2`; `bg-black/5` nao existe neste Tailwind, onde
      `black` e uma paleta). O botao direito sobre a faixa
      continua abrindo o mesmo menu (ver `openMenu`).

      O `@click.stop` no invólucro impede que o clique no menu chegue ao corpo
      da faixa, que salta para a mensagem.
    -->
    <div
      v-on-clickaway="closeMenu"
      class="relative ml-auto flex-shrink-0"
      @click.stop
    >
      <button
        type="button"
        class="acl-pinned-banner__close size-8 flex items-center justify-center rounded-full transition-colors hover:bg-n-alpha-2"
        aria-label="Ações da mensagem fixada"
        :aria-expanded="isMenuOpen"
        @click.stop="toggleMenu"
      >
        <span class="i-lucide-chevron-down size-5" />
      </button>
      <DropdownMenu
        v-if="isMenuOpen"
        :menu-items="menuItems"
        class="mt-1 top-full ltr:right-0 rtl:left-0"
        @action="handleMenuAction"
      />
    </div>
  </div>
</template>
